/**
 * API client for communicating with the backend.
 *
 * Handles:
 * - Base URL configuration from environment
 * - Automatic Authorization header injection
 * - JSON request/response handling
 * - Token refresh on 401 responses
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Set the current access token (called by auth context on login/refresh).
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Get the current access token.
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Register a token refresh handler. Called by auth context to wire up refresh logic.
 */
let refreshHandler: (() => Promise<string | null>) | null = null;

export function setRefreshHandler(handler: (() => Promise<string | null>) | null): void {
  refreshHandler = handler;
}

/**
 * Attempt to refresh the token, deduplicating concurrent refresh calls.
 */
async function attemptRefresh(): Promise<string | null> {
  if (!refreshHandler) return null;

  if (!refreshPromise) {
    refreshPromise = refreshHandler().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip automatic token injection (for login/register endpoints) */
  skipAuth?: boolean;
}

export interface ApiResult<T> {
  success: true;
  data: T;
  meta?: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export type ApiResultOrError<T> = ApiResult<T> | ApiError;

/**
 * Make an API request to the backend.
 *
 * Automatically:
 * - Adds Authorization header with current access token
 * - Retries once with refreshed token on 401
 * - Parses JSON responses
 */
export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<ApiResultOrError<T>> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  const buildHeaders = (): Record<string, string> => {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };
    if (!skipAuth && accessToken) {
      h['Authorization'] = `Bearer ${accessToken}`;
    }
    return h;
  };

  const doFetch = async (hdrs: Record<string, string>): Promise<Response> => {
    const url = `${API_BASE_URL}${endpoint}`;
    return fetch(url, {
      method,
      headers: hdrs,
      body: body ? JSON.stringify(body) : undefined,
    });
  };

  let response = await doFetch(buildHeaders());

  // If 401 and we have a refresh handler, try refreshing the token once
  if (response.status === 401 && !skipAuth && refreshHandler) {
    const newToken = await attemptRefresh();
    if (newToken) {
      accessToken = newToken;
      response = await doFetch(buildHeaders());
    }
  }

  const json = await response.json();
  return json as ApiResultOrError<T>;
}
